import { Head, router } from '@inertiajs/react'
import { Box, Container, Flex, Heading, Section, SegmentedControl } from '@radix-ui/themes'
import { List, Map as MapIcon, MapPin } from 'lucide-react'
import type { EventsIndexEvent, EventsIndexProps } from '@/types/events_index'
import { useLikes } from '@/shared/likes/useLikes'
import EventFeed, { EventRows } from '@/shared/events/EventFeed'
import LocationSearch from '@/shared/map_index/LocationSearch'
import MapListIndex from '@/shared/map_index/MapListIndex'
import type { MapMarker } from '@/shared/map_index/MapListIndex'
import ResultCard from '@/shared/map_index/ResultCard'

export default function EventsIndex(props: EventsIndexProps) {
  return props.view === 'map' ? <EventsMap {...props} /> : <EventsFeed {...props} />
}

/** Switches view, carrying the search across so you're looking at the same place. */
function ViewToggle({ view, search, distance }: Pick<EventsIndexProps, 'view' | 'search' | 'distance'>) {
  const go = (next: string) =>
    router.get('/events', {
      ...(next === 'map' ? { view: 'map' } : {}),
      ...(search ? { search, distance } : {}),
    })

  return (
    <SegmentedControl.Root value={view} onValueChange={go} size="2">
      <SegmentedControl.Item value="list">
        <span className="flex items-center gap-1"><List className="h-4 w-4" /> List</span>
      </SegmentedControl.Item>
      <SegmentedControl.Item value="map">
        <span className="flex items-center gap-1"><MapIcon className="h-4 w-4" /> Map</span>
      </SegmentedControl.Item>
    </SegmentedControl.Root>
  )
}

function EventsFeed({ your_events, upcoming, recent, search, distance, view }: EventsIndexProps) {
  return (
    <>
      <Head title="Events" />
      <Section size="1" px="4">
        <Container size="4">
          <Flex justify="between" align="center" gap="3" wrap="wrap" mb="4">
            <Heading size="8">Events</Heading>
            <ViewToggle view={view} search={search} distance={distance} />
          </Flex>

          <Box mb="8">
            <LocationSearch path="/events" initialSearch={search} initialDistance={distance} layout="inline" />
          </Box>

          {your_events.length > 0 && (
            <section className="mb-12 flex flex-col gap-4">
              <Heading as="h2" size="6">Your events</Heading>
              <EventRows events={your_events} />
            </section>
          )}

          <EventFeed
            upcoming={upcoming}
            recent={recent}
            headlinerLabel={search ? 'Next nearby' : 'Next event'}
            emptyMessage="No events found. Try widening your search."
          />
        </Container>
      </Section>
    </>
  )
}

function EventsMap({ your_events, upcoming, recent, tracks, map_center, zoom, search, distance, view }: EventsIndexProps) {
  const { likeIdFor, toggleLike } = useLikes('Event')

  // Events pin their host track, so the markers come from the tracks list
  // rather than the events themselves — several events can share one dot.
  const markers: MapMarker[] = tracks.map(track => ({
    mine: track.mine,
    longitude: track.longitude,
    latitude: track.latitude,
    popupHtml: `<a href="/tracks/${track.slug}"><strong>${track.name}</strong><p>${track.city}, ${track.state}</p><p>Click for more info</p></a>`,
  }))

  return (
    <MapListIndex<EventsIndexEvent>
      title="Events"
      path="/events?view=map"
      toolbar={<ViewToggle view={view} search={search} distance={distance} />}
      markers={markers}
      sections={[
        { label: 'Your events', items: your_events },
        { label: 'Upcoming', items: upcoming },
        { label: 'Recent', items: recent },
      ]}
      emptyMessage="No events found. Try widening your search."
      mapCenter={map_center}
      zoom={zoom}
      initialSearch={search}
      initialDistance={distance}
      coordsFor={event => [event.track_longitude, event.track_latitude]}
      renderItem={(event, { selected, onSelect }) => (
        <ResultCard
          name={event.name}
          selected={selected}
          onSelect={onSelect}
          liked={likeIdFor(event) != null}
          onToggleLike={() => toggleLike(event)}
          href={`/events/${event.id}`}
          ctaLabel="Details"
        >
          <div className="flex flex-col gap-1 text-sm text-gray11">
            <span className="flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{event.track_name}</span>
            </span>
            <span className="truncate">
              {event.date} · {event.is_past ? `${event.time_until} ago` : `in ${event.time_until}`}
            </span>
          </div>
        </ResultCard>
      )}
    />
  )
}
